import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Joyride, EVENTS, STATUS } from 'react-joyride';
import {
  BOARD_FILTERS_TOUR_TARGETS,
  getBoardFiltersTourSettings,
} from './boardFiltersTourConstants';
import { fetchTourVirtualStatus, markTourVirtualComplete } from './tourVirtualApi';
import { BoardFiltersTourTooltip } from './BoardFiltersTourTooltip';
import './BoardFiltersTour.css';

const TOUR_Z_INDEX = 15000;

const JOYRIDE_LOCALE = {
  back: 'Voltar',
  close: 'Fechar',
  last: 'Entendi',
  next: 'Próxima',
  nextWithProgress: 'Próxima ({current} de {total})',
  skip: 'Pular',
};

const JOYRIDE_OPTIONS = {
  dismissKeyAction: false,
  overlayClickAction: false,
  skipScroll: true,
  showProgress: true,
  primaryColor: '#2347ff',
  textColor: '#2d3748',
  arrowColor: '#ffffff',
  backgroundColor: '#ffffff',
  overlayColor: 'rgba(15, 23, 42, 0.5)',
  zIndex: TOUR_Z_INDEX,
};

const JOYRIDE_STYLES = {
  tooltip: {
    fontSize: 13,
    lineHeight: 1.45,
    padding: 14,
    borderRadius: 10,
    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
    transition: 'none',
  },
  tooltipTitle: { display: 'none' },
  tooltipContent: { padding: '4px 0 0' },
  buttonPrimary: { fontSize: 12, fontWeight: 600, borderRadius: 6, padding: '6px 12px' },
  buttonBack: { fontSize: 12, marginRight: 8 },
};

function waitForTarget(selector, maxMs = 6000) {
  return new Promise((resolve) => {
    if (document.querySelector(selector)) {
      resolve(true);
      return;
    }
    const started = Date.now();
    const id = window.setInterval(() => {
      if (document.querySelector(selector)) {
        window.clearInterval(id);
        resolve(true);
      } else if (Date.now() - started >= maxMs) {
        window.clearInterval(id);
        resolve(false);
      }
    }, 100);
  });
}

function buildMenuStepBefore(ensureMenuOpen, target) {
  return async () => {
    ensureMenuOpen?.();
    await waitForTarget(target, 5000);
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
  };
}

function buildSteps(ensureMenuOpen) {
  return [
    {
      target: BOARD_FILTERS_TOUR_TARGETS.TRIGGER,
      title: 'Filtros centralizados',
      content: 'Os filtros das colunas Em aberto e de técnicos agora estão todos aqui.',
      placement: 'bottom',
      offset: 14,
      skipBeacon: true,
      buttons: [],
      overlayClickAction: false,
      blockTargetInteraction: false,
      data: { noButtons: true, clickToContinue: true },
    },
    {
      target: BOARD_FILTERS_TOUR_TARGETS.SCOPE_GROUP,
      title: 'Onde aplicar',
      content: 'Escolha em qual parte do board os filtros serão aplicados.',
      placement: 'bottom',
      skipBeacon: true,
      before: buildMenuStepBefore(ensureMenuOpen, BOARD_FILTERS_TOUR_TARGETS.SCOPE_GROUP),
    },
    {
      target: BOARD_FILTERS_TOUR_TARGETS.SCOPE_EM_ABERTO,
      title: 'Em aberto',
      content: 'Quando marcado aqui, filtra os dados apenas da coluna Em aberto.',
      placement: 'bottom',
      spotlightPadding: 6,
      skipBeacon: true,
      before: buildMenuStepBefore(ensureMenuOpen, BOARD_FILTERS_TOUR_TARGETS.SCOPE_EM_ABERTO),
    },
    {
      target: BOARD_FILTERS_TOUR_TARGETS.SCOPE_TECNICOS,
      title: 'Técnicos',
      content: 'Quando marcado aqui, filtra os dados apenas nas colunas de técnicos.',
      placement: 'bottom',
      spotlightPadding: 6,
      skipBeacon: true,
      before: buildMenuStepBefore(ensureMenuOpen, BOARD_FILTERS_TOUR_TARGETS.SCOPE_TECNICOS),
    },
    {
      target: BOARD_FILTERS_TOUR_TARGETS.SCOPE_AMBOS,
      title: 'Ambos',
      content:
        'Quando marcado aqui, filtra os dados de qualquer coluna, desde que as colunas de técnicos estejam exibidas.',
      placement: 'bottom',
      spotlightPadding: 6,
      skipBeacon: true,
      before: buildMenuStepBefore(ensureMenuOpen, BOARD_FILTERS_TOUR_TARGETS.SCOPE_AMBOS),
    },
    {
      target: BOARD_FILTERS_TOUR_TARGETS.CHIPS,
      title: 'Opções de filtro',
      content: 'Os mesmos filtros de antes continuam disponíveis aqui, organizados por categoria.',
      placement: 'left',
      skipBeacon: true,
      before: buildMenuStepBefore(ensureMenuOpen, BOARD_FILTERS_TOUR_TARGETS.CHIPS),
    },
    {
      target: BOARD_FILTERS_TOUR_TARGETS.CHIPS,
      title: 'Tudo certo?',
      content: 'Você está ciente das alterações? Clique em Entendi para confirmar. Bom uso!',
      placement: 'left',
      skipBeacon: true,
      before: buildMenuStepBefore(ensureMenuOpen, BOARD_FILTERS_TOUR_TARGETS.CHIPS),
    },
  ];
}

export function BoardFiltersTour({ menuOpen, onEnsureMenuOpen, onActiveChange, onTourEnd }) {
  const [run, setRun] = useState(false);
  const controlsRef = useRef(null);
  const stepIndexRef = useRef(0);
  const prevMenuOpenRef = useRef(false);
  const completingRef = useRef(false);

  const ensureMenuOpen = useCallback(() => {
    onEnsureMenuOpen?.();
  }, [onEnsureMenuOpen]);

  const steps = useMemo(() => buildSteps(ensureMenuOpen), [ensureMenuOpen]);

  const finishTour = useCallback(async () => {
    if (completingRef.current) return;
    completingRef.current = true;
    controlsRef.current?.stop?.();
    setRun(false);
    stepIndexRef.current = 0;
    onTourEnd?.();
    try {
      const { tourKey, tourVersion } = getBoardFiltersTourSettings();
      if (tourKey && tourVersion) {
        await markTourVirtualComplete(tourKey, tourVersion);
      }
    } catch (_) {
      /* persistência opcional */
    }
  }, [onTourEnd]);

  // ── Inicialização ───────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    let timerId = null;

    (async () => {
      const { active, tourKey, tourVersion } = getBoardFiltersTourSettings();
      if (!active || !tourVersion) return;

      try {
        const completed = await fetchTourVirtualStatus(tourKey, tourVersion);
        if (cancelled || completed) return;

        const ready = await waitForTarget(BOARD_FILTERS_TOUR_TARGETS.TRIGGER);
        if (cancelled || !ready) return;

        timerId = window.setTimeout(() => {
          if (!cancelled) {
            prevMenuOpenRef.current = false;
            setRun(true);
          }
        }, 400);
      } catch (_) {
        /* MySQL indisponível — não exibe tour */
      }
    })();

    return () => {
      cancelled = true;
      if (timerId != null) window.clearTimeout(timerId);
    };
  }, []);

  useEffect(() => {
    onActiveChange?.(run);
  }, [run, onActiveChange]);

  // ── Passo 0 → 1: avança quando menu abre (false → true) ────────────────────
  useEffect(() => {
    if (!run) {
      prevMenuOpenRef.current = false;
      return;
    }

    const wasOpen = prevMenuOpenRef.current;
    prevMenuOpenRef.current = menuOpen;

    if (stepIndexRef.current !== 0 || !menuOpen || wasOpen) return;

    (async () => {
      await waitForTarget(BOARD_FILTERS_TOUR_TARGETS.SCOPE_GROUP, 5000);
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      if (stepIndexRef.current === 0) {
        controlsRef.current?.next?.();
      }
    })();
  }, [run, menuOpen]);

  // ── Passos 1+: garante menu aberto ────────────────────────────────────────
  useEffect(() => {
    if (!run || stepIndexRef.current < 1) return;
    if (!menuOpen) ensureMenuOpen();
  }, [run, menuOpen, ensureMenuOpen]);

  // ── Joyride v3: onEvent (não callback) ────────────────────────────────────
  const handleEvent = useCallback(
    (data, controls) => {
      controlsRef.current = controls;

      const { index, status, type } = data;
      if (typeof index === 'number') {
        stepIndexRef.current = index;
      }

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        finishTour();
        return;
      }

      if (type === EVENTS.TARGET_NOT_FOUND) {
        ensureMenuOpen();
      }
    },
    [finishTour, ensureMenuOpen]
  );

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      scrollToFirstStep
      onEvent={handleEvent}
      locale={JOYRIDE_LOCALE}
      options={JOYRIDE_OPTIONS}
      styles={JOYRIDE_STYLES}
      tooltipComponent={BoardFiltersTourTooltip}
      floatingOptions={{ offset: 12 }}
    />
  );
}
